/**
 * The following constructs are adapted/borrowed from the ideas in this blog post:
 *
 * https://medium.com/with-orus/the-5-commandments-of-clean-error-handling-in-typescript-93a9cbdf1af5
 *
 */

/**
 * When running typescript in strict mode the return type of a try-catch block
 * error moves from 'any' to 'unknown'. This function ensures that the error
 * thrown is an instance of Error and if not, it creates a new Error instance
 * and reduces boilerplate code in try-catch blocks.
 */
export const ensureError = (value: unknown): Error => {
  if (value instanceof Error) return value

  let stringified = '[Unable to stringify the thrown value]'
  try {
    stringified = JSON.stringify(value)
  } catch {
    /* leave stringified with original value */
  }

  const error = new Error(
    `This value was thrown as is, not through an Error: ${stringified}`,
  )
  return error
}

type Jsonable =
  | string
  | number
  | boolean
  | null
  | undefined
  | Date
  | readonly Jsonable[]
  | { readonly [key: string]: Jsonable }
  | { toJSON(): Jsonable }

/**
 * A custom DroidmapError class that captures additional context information.
 */
export class DroidMapError extends Error {
  public readonly context?: Jsonable

  constructor(
    message: string,
    options: { cause?: Error; context?: Jsonable } = {},
  ) {
    const { cause, context } = options

    super(message, { cause })
    this.name = this.constructor.name

    this.context = context
  }
}

/**
 * A custom error type for when a entity is not found.
 */
export class EntityNotFoundError extends DroidMapError {
  constructor(
    message: string,
    options: { cause?: Error; context?: Jsonable } = {},
  ) {
    super(message, options)
  }
}

/**
 * A custom error type for when a permission check fails.
 */
export class PermissionDeniedError extends DroidMapError {
  constructor(
    message: string,
    options: { cause?: Error; context?: Jsonable } = {},
  ) {
    super(message, options)
  }
}

/**
 * A custom error type for when an entity already exists.
 */
export class EntityAlreadyExistsError extends DroidMapError {
  /**
   * The refusal the *service* sent, when this error was reconstructed from a
   * 409 response rather than thrown locally. See
   * {@link HttpResponseError.serverMessage} — same contract, and the reason
   * it is a separate field from `message` even though the two now agree on
   * this path: only a value that came from a response body is safe to render
   * to a user, and `message` alone cannot say where it came from.
   */
  public readonly serverMessage?: string

  constructor(
    message: string,
    options: { cause?: Error; context?: Jsonable; serverMessage?: string } = {},
  ) {
    super(message, options)
    this.serverMessage = options.serverMessage
  }
}

/**
 * A billable-resource limit was reached and the action was refused (e.g.
 * the org's storage allowance is full — #1986). Distinct from
 * `PermissionDeniedError`: nothing is wrong with the caller's *permissions*
 * — the resolution is commercial (upgrade the plan, buy overage, raise a
 * self-set ceiling), so the shared Hono error handler maps it to
 * 402 Payment Required rather than 403.
 */
export class ResourceLimitExceededError extends DroidMapError {
  /**
   * Machine-readable reason the limit was hit (e.g. `spend_limit_reached`,
   * `bundle_exhausted`, `budget_reached`). Surfaced on the 402
   * response body by the shared Hono error handler so the client can
   * deep-link to the right billing remedy. Optional — omit when a single
   * generic 402 is enough.
   */
  public readonly reason?: string

  constructor(
    message: string,
    options: { cause?: Error; context?: Jsonable; reason?: string } = {},
  ) {
    super(message, options)
    this.reason = options.reason
  }
}

/**
 * Access to an organisation is frozen and the whole org is gated (#2046,
 * ADR-0013 revision). Raised by the org-service frozen-org gate once the
 * Starter trial sweeper (#2045) has flipped an expired trial to `frozen`:
 * unlike {@link ResourceLimitExceededError} (a 402 on a single cost-driver
 * *action*), this blocks the *org as a whole* — reads included — until the
 * trial is upgraded, so the shared Hono error handler maps it to 403 Forbidden
 * rather than 402. The read and upgrade/payment paths the frozen-state CTA
 * needs are allow-listed by the gate and never raise this.
 */
export class OrgFrozenError extends DroidMapError {
  /**
   * Machine-readable reason the org is gated (currently always
   * `'trial_expired'`). Surfaced on the 403 response body by the shared Hono
   * error handler so the client can render the right "trial ended — upgrade"
   * CTA. Optional — omit when a single generic 403 is enough.
   */
  public readonly reason?: string

  constructor(
    message: string,
    options: { cause?: Error; context?: Jsonable; reason?: string } = {},
  ) {
    super(message, options)
    this.reason = options.reason
  }
}

/**
 * A request was well-formed for its route schema but violated a domain
 * invariant the schema can't express — e.g. a value below a tier-dependent
 * floor (a Pro auto-recharge rule set below the plan's minimum). Distinct from
 * `PermissionDeniedError` (the caller is allowed) and route-level Zod
 * validation (which has no domain/catalogue context): the shared Hono error
 * handler maps any bare {@link DroidMapError} — this included — to
 * 400 Bad Request.
 */
export class ValidationError extends DroidMapError {
  constructor(
    message: string,
    options: { cause?: Error; context?: Jsonable } = {},
  ) {
    super(message, options)
  }
}

/**
 * A request to a DroidMap service came back with a non-OK HTTP status (or ran
 * out of retries trying). Carries {@link status} so callers can tell the one
 * distinction that matters at a UI boundary: *the thing isn't there* (404 —
 * or 403, "not yours to see") versus *the call failed* (5xx, 429, a token
 * refresh landing mid-flight).
 *
 * That distinction was previously expressible only in the message text, so
 * every caller either parsed a string or — far more commonly — collapsed the
 * two and reported a transient blip as a missing entity (droidmap#2658: a 5xx
 * on the org route was shown to the user as "Organization not found", a false
 * claim about their data with no way back).
 *
 * `status` is optional because not every failure has one: retries can be
 * exhausted with nothing but network errors to show for it. Treat an absent
 * status as transient — never as "not found". {@link isHttpStatusError} does.
 *
 * Extends {@link DroidMapError}, so server-side handlers that branch on
 * `instanceof DroidMapError` are unaffected.
 *
 * @example
 * try {
 *   await organisationClient.getOrganisation({ orgId }, metadata)
 * } catch (error) {
 *   if (isHttpStatusError(error, HttpStatus.NOT_FOUND_404)) return null
 *   throw error // transient — let the caller retry or surface an error
 * }
 */
export class HttpResponseError extends DroidMapError {
  /** The response status, when the failure had one. */
  public readonly status?: number

  /**
   * The `error` field of the service's JSON body — the refusal in the
   * *server's* own words — when it sent one.
   *
   * Deliberately not folded into {@link message}, which stays a transport
   * description ("Failed to call api route … - 400 Bad Request"). That string
   * is an internal one nothing should ever render (`LoadFailed` exists in the
   * web app precisely because the root error card used to), so a UI needs a
   * field it can point at and know the text came from a response body rather
   * than from the client's own plumbing. This is that field.
   *
   * Undefined when the body was empty or not JSON (an ALB or CloudFront error
   * page), or when retries were exhausted with no response to read at all.
   * Present does **not** mean fit to show: a 404 carries `'Not found'` and a
   * 5xx `'Internal server error'`, neither of which tells a user anything.
   * {@link serverRefusalMessage} is the gate for that decision — prefer it
   * over reading this directly.
   */
  public readonly serverMessage?: string

  /**
   * The `reason` field of the service's JSON body, when it sent one — the
   * machine-readable half of a 402 quota refusal (`bundle_exhausted`,
   * `spend_limit_reached`, …; see {@link ResourceLimitExceededError.reason}).
   * A client keys its remedy copy and billing deep link off this rather than
   * parsing {@link serverMessage}. A plain `string`: the wire carries no
   * guarantee the value is one the client knows, so narrow before indexing.
   */
  public readonly serverReason?: string

  constructor(
    message: string,
    options: {
      status?: number
      cause?: Error
      context?: Jsonable
      serverMessage?: string
      serverReason?: string
    } = {},
  ) {
    super(message, options)
    this.status = options.status
    this.serverMessage = options.serverMessage
    this.serverReason = options.serverReason
  }
}

/**
 * Is `error` an {@link HttpResponseError} carrying one of `statuses`?
 *
 * Deliberately a *positive* test on a typed error rather than a negative test
 * on a message or a status range: anything that isn't a recognised status —
 * a bare `TypeError: Failed to fetch` from a browser network blip, a rejected
 * token refresh, retries exhausted with no status — answers `false` and so
 * falls to the caller's transient branch. Getting that polarity backwards is
 * exactly how a blip becomes a permanent-looking "not found".
 */
export const isHttpStatusError = (
  error: unknown,
  ...statuses: number[]
): boolean =>
  error instanceof HttpResponseError &&
  error.status !== undefined &&
  statuses.includes(error.status)
